<template>
  <q-card
    class="full-height bg-white"
    style="display: flex; flex-direction: column; min-height: 50vh; max-height: 85vh; width: 100%"
  >
    <!-- HEADER AREA -->
    <div class="top-z bg-white shadow-2" style="flex: 0 0 auto">
      <q-tabs
        v-model="activeTab"
        dense
        class="text-grey-8 bg-grey-2"
        active-color="primary"
        indicator-color="primary"
        align="justify"
      >
        <q-tab name="pbp" label="Play-by-Play" icon="list_alt" />
        <q-tab name="hits" label="Hitting" icon="sports_baseball" />
        <q-tab name="pitching" label="Pitching" icon="speed" />
        <q-tab name="players" label="Player Summary" icon="person" />
      </q-tabs>

      <q-separator />

      <div class="q-pa-sm row justify-center bg-grey-1" style="border-bottom: 1px solid #e0e0e0">
        <q-btn-toggle
          v-model="teamFilter"
          rounded
          unelevated
          no-caps
          toggle-color="primary"
          color="white"
          text-color="grey-8"
          :options="teamFilterOptions"
          size="sm"
          class="shadow-1 text-weight-bold"
        />
      </div>
    </div>

    <!-- MAIN PANELS -->
    <q-tab-panels
      v-model="activeTab"
      animated
      class="bg-white hide-scrollbar"
      style="flex: 1 1 auto; min-height: 0; overflow-y: auto"
    >
      <!-- PLAY BY PLAY -->
      <q-tab-panel name="pbp" class="q-pa-none">
        <q-list separator>
          <q-item v-for="n in filteredNarratives" :key="n.paId" class="q-py-md">
            <q-item-section>
              <div class="row justify-between items-center q-mb-xs">
                <div class="text-subtitle2 text-primary">{{ n.inning }}</div>
                <div class="text-caption text-grey-6 text-weight-bold">PA #{{ n.paNumber }}</div>
              </div>
              <div class="text-weight-bold q-mb-xs">{{ n.pitcher }} pitching to {{ n.batter }}</div>
              <div class="text-body2 text-grey-9 q-mt-xs">{{ n.summary }}</div>

              <div
                v-if="n.runners || n.play || n.pitches"
                class="row q-gutter-x-sm text-caption text-grey-7 q-mt-sm"
              >
                <q-chip v-if="n.pitches" size="sm" color="grey-2" class="q-ma-none">
                  {{ n.pitches.length }} Pitches
                </q-chip>
                <q-chip v-if="n.play" size="sm" color="grey-2" class="q-ma-none">
                  Play: {{ n.play }}
                </q-chip>
              </div>
            </q-item-section>
          </q-item>

          <div v-if="filteredNarratives.length === 0" class="q-pa-xl text-center text-grey-6">
            <q-icon name="sports_baseball" size="xl" color="grey-4" class="q-mb-sm" />
            <div>No plays recorded yet.</div>
          </div>
        </q-list>
      </q-tab-panel>

      <!-- HITTING TAB (REPLACES LINEUP & HITS) -->
      <q-tab-panel name="hits" class="q-pa-none">
        <GameSummaryHitting :team-lineup="teamFilter"></GameSummaryHitting>
      </q-tab-panel>

      <!-- PITCHING TAB -->
      <q-tab-panel name="pitching" class="q-pa-none">
        <GameSummaryPitching :team-lineup="teamFilter"></GameSummaryPitching>
      </q-tab-panel>

      <!-- PLAYER SUMMARY TAB -->
      <q-tab-panel name="players" class="q-pa-none">
        <GameSummaryPlayer :team-lineup="teamFilter"></GameSummaryPlayer>
      </q-tab-panel>
    </q-tab-panels>
  </q-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import GameSummaryPitching from 'components/GameSummaryPitching.vue'
import GameSummaryHitting from 'components/GameSummaryHitting.vue'
import GameSummaryPlayer from 'components/GameSummaryPlayer.vue'
import { useGameStore } from 'stores/game-store'
const gameStore = useGameStore()

const activeTab = ref('hits') // Defaulted to new Hitting view
const teamFilter = ref<'all' | 'away' | 'home'>('away')

// --- DYNAMIC FILTER OPTIONS ---
const teamFilterOptions = computed(() => {
  const opts = [
    { label: 'Away', value: 'away' },
    { label: 'Home', value: 'home' },
  ]
  if (activeTab.value === 'pbp' || activeTab.value === 'players') {
    opts.unshift({ label: 'All', value: 'all' })
  }
  return opts
})

watch(activeTab, (newTab) => {
  // Enforce filtering if switching back to Hitting or Pitching
  if ((newTab === 'hits' || newTab === 'pitching') && teamFilter.value === 'all') {
    teamFilter.value = 'away'
  }
})

// --- PLAY BY PLAY LOGIC ---
const allNarratives = computed(() => gameStore.generateGameNarrative() || [])

const filteredNarratives = computed(() => {
  if (teamFilter.value === 'all') return allNarratives.value
  return allNarratives.value.filter((n) => {
    const isTop = n.inning.toLowerCase().includes('top')
    return teamFilter.value === 'away' ? isTop : !isTop
  })
})


</script>
<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-z {
  z-index: 10;
}
</style>
